import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import FhirSearchService from '@lifeomic/phc-web-toolkit/src/services/FhirSearchService';
import useServiceAuth from '@lifeomic/phc-web-toolkit/src/hooks/useServiceAuth';
import { authSelectors } from '@lifeomic/phc-web-toolkit/src/redux/modules/auth';
import { projectCohortPickerSelectors } from '@lifeomic/phc-web-toolkit/src/redux/modules/projectCohortPicker';
import {
  buildPatientIdKeywordSelectQuery,
  cohortContainsPatientBySubjectIds,
  type CohortLike,
} from './memberCohortMembership';
import type { ServiceAuth } from '@lifeomic/phc-web-toolkit/src/hooks/useService/useService';

const fhirSearchService = new FhirSearchService();

type FhirSearchHits = {
  hits?: { hits?: unknown[] };
};

const patientMatchesCohortSearch = async (
  auth: ServiceAuth,
  projectId: string,
  cohortId: string,
  patientId: string,
): Promise<boolean> => {
  const response = await fhirSearchService.search(auth)(
    projectId,
    buildPatientIdKeywordSelectQuery(patientId),
    undefined,
    { queryId: 'member-detail-patient-in-cohort' },
    cohortId,
  );
  const data = response.data as FhirSearchHits | undefined;
  return (data?.hits?.hits?.length ?? 0) > 0;
};

export type MemberHomeCohort = { id: string; name: string };

export type UseMemberOutsideActiveCohortParams = {
  projectId?: string;
  patientId?: string;
  /** Null or undefined means “All Cohorts”. */
  activeCohortId?: string | null;
};

export type UseMemberOutsideActiveCohortResult = {
  /** True when a specific cohort is selected and the patient is not in it (search succeeded). */
  showMismatch: boolean;
  /** A different cohort that includes this patient, when discoverable. */
  homeCohort: MemberHomeCohort | null;
  isFindingHomeCohort: boolean;
};

/**
 * When the global cohort filter is set to a cohort, verifies via FHIR search
 * whether the active member is in that cohort, and optionally discovers another
 * cohort that contains the member (for “switch to that cohort”).
 */
export const useMemberOutsideActiveCohort = ({
  projectId,
  patientId,
  activeCohortId,
}: UseMemberOutsideActiveCohortParams): UseMemberOutsideActiveCohortResult => {
  const auth = useServiceAuth();
  const accessToken = useSelector(authSelectors.selectAccessToken);
  const selectableCohorts = useSelector(
    projectCohortPickerSelectors.selectableCohorts,
  ) as CohortLike[];

  const [inActiveCohort, setInActiveCohort] = useState<boolean | null>(null);
  const [homeCohort, setHomeCohort] = useState<MemberHomeCohort | null>(null);
  const [isFindingHomeCohort, setIsFindingHomeCohort] = useState(false);

  const cohortKey = activeCohortId || '';
  const patientKey = patientId || '';
  const projectKey = projectId || '';

  useEffect(() => {
    setInActiveCohort(null);
    setHomeCohort(null);
    setIsFindingHomeCohort(false);
  }, [cohortKey, patientKey, projectKey]);

  useEffect(() => {
    if (!activeCohortId || !patientId || !projectId || !accessToken) {
      setInActiveCohort(null);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const ok = await patientMatchesCohortSearch(
          auth,
          projectId,
          activeCohortId,
          patientId,
        );
        if (!cancelled) {
          setInActiveCohort(ok);
        }
      } catch {
        // Verification failed; do not infer "not in cohort" (would mislead clinicians).
        if (!cancelled) {
          setInActiveCohort(null);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [activeCohortId, patientId, projectId, accessToken, auth]);

  useEffect(() => {
    if (
      !activeCohortId ||
      !patientId ||
      !projectId ||
      !accessToken ||
      inActiveCohort !== false
    ) {
      return;
    }

    let cancelled = false;
    setHomeCohort(null);
    setIsFindingHomeCohort(true);

    const others = (selectableCohorts || []).filter(
      c => c.id && c.id !== activeCohortId,
    );

    (async () => {
      for (const c of others) {
        if (cancelled) {
          return;
        }
        const bySubjects = cohortContainsPatientBySubjectIds(c, patientId);
        if (bySubjects === true) {
          if (!cancelled) {
            setHomeCohort({ id: c.id, name: c.name });
            setIsFindingHomeCohort(false);
          }
          return;
        }
      }

      for (const c of others) {
        if (cancelled) {
          return;
        }
        const bySubjects = cohortContainsPatientBySubjectIds(c, patientId);
        if (bySubjects === false) {
          continue;
        }
        try {
          // subjectIds absent/empty — need a cohort-scoped search
          if (
            await patientMatchesCohortSearch(auth, projectId, c.id, patientId)
          ) {
            if (!cancelled) {
              setHomeCohort({ id: c.id, name: c.name });
              setIsFindingHomeCohort(false);
            }
            return;
          }
        } catch {
          // ignore and continue scanning cohorts
        }
      }

      if (!cancelled) {
        setIsFindingHomeCohort(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    activeCohortId,
    patientId,
    projectId,
    accessToken,
    auth,
    inActiveCohort,
    selectableCohorts,
  ]);

  const showMismatch =
    Boolean(activeCohortId && patientId && projectId) &&
    inActiveCohort === false;

  return {
    showMismatch,
    homeCohort,
    isFindingHomeCohort,
  };
};
